import React, {useState, useEffect, useCallback} from 'react';
import {Box} from '@mui/material';
import {toast} from 'react-toastify';

import {useWebSocket} from 'hooks/useWebSocket';
import {buildApi} from 'utils/api';

import BuildForm from './BuildForm';
import BuildLogs from './BuildLogs';
import ActiveBuilds from './ActiveBuilds';

export default function MainPage() {
    const [builds, setBuilds] = useState([]);
    const [queue, setQueue] = useState(null);
    const [currentBuild, setCurrentBuild] = useState(null);

    const handleWsMessage = useCallback((data) => {
        if (data.type === 'logs' && currentBuild?.id === data.buildId && Array.isArray(data.logs)) {
            setCurrentBuild(prev => ({
                ...prev,
                logs: [...(prev?.logs || []), ...data.logs],
            }));
        }

        if (data.type === 'status') {
            if (currentBuild?.id === data.buildId) {
                loadBuild(data.buildId);
            }
            loadBuilds();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBuild?.id]);

    const {subscribe, unsubscribe} = useWebSocket(handleWsMessage);

    useEffect(() => {
        loadBuilds();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentBuild?.id) {
            subscribe(currentBuild.id);
            return () => unsubscribe(currentBuild.id);
        }
    }, [currentBuild?.id, subscribe, unsubscribe]);

    const loadBuilds = async () => {
        try {
            const {data} = await buildApi.getBuilds(100, 1, {status: 'pending,running'});
            setBuilds(data.builds || []);
            setQueue(data.queue);
        } catch (err) {
            console.error('Failed to load builds:', err);
        }
    };

    const loadBuild = async (buildId) => {
        try {
            const {data} = await buildApi.getBuild(buildId);
            setCurrentBuild(data.build);
        } catch (err) {
            toast.error('빌드 정보를 불러올 수 없습니다.');
        }
    };

    const handleBuildCreated = (build) => {
        loadBuilds();
        loadBuild(build.id);
    };

    const handleViewBuild = (buildId) => {
        loadBuild(buildId);
    };

    const handleCancelBuild = async (buildId) => {
        if (!window.confirm('빌드를 취소하시겠습니까?')) return;

        try {
            await buildApi.cancelBuild(buildId);
            toast.success('빌드가 취소되었습니다.');
            loadBuilds();
            if (currentBuild?.id === buildId) {
                loadBuild(buildId);
            }
        } catch (err) {
            toast.error(err.response?.data?.error || '빌드 취소 실패');
        }
    };

    const handleCloseBuildLogs = () => {
        setCurrentBuild(null);
    };

    return (
        <Box>
            <BuildForm onBuildCreated={handleBuildCreated}/>

            <ActiveBuilds
                builds={builds}
                queue={queue}
                onViewBuild={handleViewBuild}
                onCancelBuild={handleCancelBuild}
            />

            {currentBuild && (
                <BuildLogs
                    build={currentBuild}
                    onClose={handleCloseBuildLogs}
                />
            )}
        </Box>
    );
}
